'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';

import { TimePicker, TimePickerProps } from '../time-picker';

export interface FormTimePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<TimePickerProps, 'name' | 'value' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * TimePicker component integrated with React Hook Form
 * Automatically handles form registration and validation
 *
 * @example
 * ```tsx
 * <FormTimePicker
 *   name="appointmentTime"
 *   control={control}
 *   label="Appointment Time"
 *   rules={{
 *     required: 'Please select a time',
 *     validate: (value) => {
 *       const [hours] = value.split(':');
 *       const hour = parseInt(hours);
 *       return (hour >= 9 && hour < 17) || 'Appointments are only available 9 AM - 5 PM';
 *     }
 *   }}
 *   min="09:00"
 *   max="17:00"
 *   step={15}
 * />
 * ```
 */
export function FormTimePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormTimePickerProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  return (
    <TimePicker
      {...props}
      ref={ref}
      value={value}
      onChange={(time) => {
        onChange(time);
        onBlur();
      }}
      error={showError ? error?.message : undefined}
    />
  );
}

FormTimePicker.displayName = 'FormTimePicker';
